import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import { api } from '@/utils/api';

import FreelancerCommentModal from './FreelancerCommentModal';

const Box = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px;
`;

const StyledText = styled(Text)`
  margin-bottom: 8px;
`;

const Footer = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  height: 38px;
  padding: 0 16px;
`;

interface Props {
  departmentId: number;
  comment?: string;
}

const FreelancerWagesComment: FC<Props> = ({ departmentId, comment }) => {
  const [showModal, setShowModal] = useState(false);

  const userCanManageData = useHasPermission('manage freelancers');

  const { id } = useParams();

  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to do delete this comment?',
      );

      if (!confirmed) return;

      await api.post(
        `/v1/payments/freelancers/department/delete-wages-comment`,
        {
          freelancer_department_details_id: departmentId,
        },
      );

      await queryClient.invalidateQueries(['freelancers_details', { id }]);

      alerts.success('Comment has been successfully deleted');
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <>
      <Box>
        <StyledText $type="caption">General Comment</StyledText>
        <Text $color={`${comment ? 'black' : 'gray'}`} $type="smallHeadline">
          {comment ? comment : 'No comments added'}
        </Text>
        {userCanManageData && (
          <Footer>
            <StyledButton
              color="secondary"
              small
              onClick={() => setShowModal(true)}
            >
              {comment ? 'Edit' : 'Add'}
            </StyledButton>
            {comment && (
              <StyledButton onClick={onDelete} color="red" small>
                Delete
              </StyledButton>
            )}
          </Footer>
        )}
      </Box>
      <AnimatePresence>
        {showModal && (
          <FreelancerCommentModal
            departmentId={departmentId}
            comment={comment}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FreelancerWagesComment;
